/**
 * AuthorImportDialog — file picker + preview + workspace replace warning + commit.
 *
 * Flow:
 *   1. INITIAL  — user clicks "Choose JSON file" → opens native file picker.
 *   2. VALIDATING — file.text() → JSON.parse → AuthorImportService.validate.
 *      Errors render as a list with a "Choose another file" button.
 *   3. PREVIEW  — shows creator name, platforms (with names + post counts),
 *      total post / pillar / template / example / format counts. If a
 *      current author exists in the target project, a red warning Alert
 *      explains the import will permanently replace that workspace.
 *   4. IMPORTING — shows progress messages from the service's onProgress
 *      callback. Cancel button is disabled during this phase.
 *   5. SUCCESS  — dialog auto-closes after toast.
 *
 * The Import button is the explicit confirm — there is no separate
 * "are you sure?" step; the red Alert above the button IS the warning.
 *
 * The same dialog handles both "fresh import" (empty target project) and
 * "replace import" (target has an existing current author). The only
 * visual difference is the Alert severity and text.
 */

import React, { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { SDialog } from '@modules/ui-components/dialogs/SDialog/SDialog';
import {
  authorImportService,
  validateAuthorExport,
  type ImportPreviewSummary,
} from '../../services/AuthorImportService';
import type { AuthorDoc } from '../../services/AuthorFirestoreService';

const TESTID = 'creator-import-dialog';

type Phase = 'initial' | 'validating' | 'invalid' | 'preview' | 'importing' | 'success';

export interface AuthorImportDialogProps {
  open: boolean;
  onClose: () => void;
  /** Target project id — where the imported author will be written. */
  projectId: string;
  /** Currently-loaded author workspace in the target project (or null). Drives the "replace" warning copy. */
  currentAuthor: AuthorDoc | null;
  /** Optional callback after a successful import (e.g. to fire a toast in the parent). */
  onSuccess?: (summary: ImportPreviewSummary) => void;
}

export function AuthorImportDialog(props: AuthorImportDialogProps) {
  const { open, onClose, projectId, currentAuthor, onSuccess } = props;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>('initial');
  const [errors, setErrors] = useState<string[]>([]);
  const [summary, setSummary] = useState<ImportPreviewSummary | null>(null);
  const [envelope, setEnvelope] = useState<unknown>(null);
  const [progressMsg, setProgressMsg] = useState<string>('');

  const reset = useCallback(() => {
    setPhase('initial');
    setErrors([]);
    setSummary(null);
    setEnvelope(null);
    setProgressMsg('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = useCallback(() => {
    if (phase === 'importing') return; // disable mid-import close
    reset();
    onClose();
  }, [phase, reset, onClose]);

  const handlePickFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhase('validating');
    setErrors([]);
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const result = validateAuthorExport(json);
      if (!result.valid || !result.envelope || !result.summary) {
        setErrors(result.errors.length > 0 ? result.errors : ['Invalid JSON file.']);
        setPhase('invalid');
        return;
      }
      setEnvelope(result.envelope);
      setSummary(result.summary);
      setPhase('preview');
    } catch (err) {
      setErrors([`Failed to parse JSON: ${err instanceof Error ? err.message : String(err)}`]);
      setPhase('invalid');
    }
    // Always reset the file input so re-uploading the same file fires onChange again.
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleConfirmImport = useCallback(async () => {
    if (!envelope || !summary) return;
    setPhase('importing');
    setProgressMsg('Starting import…');
    try {
      const env = envelope as { payload: Parameters<typeof authorImportService.replaceAuthor>[0]['payload'] };
      await authorImportService.replaceAuthor({
        targetProjectId: projectId,
        payload: env.payload,
        replaceAuthorId: currentAuthor?.id ?? null,
        makeDefault: currentAuthor?.isDefault,
        onProgress: (msg) => setProgressMsg(msg),
      });
      setPhase('success');
      onSuccess?.(summary);
      // Brief delay so the user sees "Done." before close.
      setTimeout(() => {
        reset();
        onClose();
      }, 700);
    } catch (err) {
      console.error('[AuthorImport] replaceAuthor failed', err);
      setErrors([`Import failed: ${err instanceof Error ? err.message : String(err)}`]);
      setPhase('invalid');
    }
  }, [envelope, summary, projectId, currentAuthor, onSuccess, reset, onClose]);

  const isReplace = currentAuthor !== null;
  const importing = phase === 'importing';

  return (
    <SDialog
      open={open}
      onClose={handleClose}
      title="Import creator from JSON"
      maxWidth="sm"
      fullWidth
      data-testid={TESTID}
      showCloseButton={!importing}
      secondaryAction={
        phase === 'preview' || phase === 'invalid'
          ? {
              label: 'Cancel',
              onClick: handleClose,
              variant: 'outlined',
              disabled: importing,
            }
          : undefined
      }
      primaryAction={
        phase === 'preview' && summary
          ? {
              label: isReplace ? 'Delete current and import' : 'Import',
              onClick: handleConfirmImport,
              variant: 'contained',
              color: isReplace ? 'error' : 'primary',
              loading: false,
              'data-testid': 'creator-import-confirm-button' as never,
            }
          : undefined
      }
    >
      <DialogContent>
        <input
          type="file"
          ref={fileInputRef}
          accept=".json,application/json"
          style={{ display: 'none' }}
          onChange={handleFileChange}
          data-testid="creator-import-file-input"
        />

        {/* Phase 1 — initial */}
        {phase === 'initial' && (
          <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Pick a Segmently creator export (.json). The file should have been
              created via the "Export creator" button in another project.
            </Typography>
            <Button
              variant="contained"
              startIcon={<FileUploadIcon />}
              onClick={handlePickFile}
              data-testid="creator-import-pick-file-btn"
            >
              Choose JSON file
            </Button>
          </Stack>
        )}

        {/* Phase 2 — validating */}
        {phase === 'validating' && (
          <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">Validating file…</Typography>
          </Stack>
        )}

        {/* Phase 3 — invalid */}
        {phase === 'invalid' && (
          <Stack spacing={2} sx={{ py: 2 }}>
            <Alert severity="error">
              <Typography variant="subtitle2" gutterBottom>This file can't be imported.</Typography>
              <List dense disablePadding>
                {errors.map((err, idx) => (
                  <ListItem key={idx} disablePadding sx={{ display: 'list-item', listStyle: 'disc', ml: 2 }}>
                    <Typography variant="body2">{err}</Typography>
                  </ListItem>
                ))}
              </List>
            </Alert>
            <Button
              variant="outlined"
              startIcon={<FileUploadIcon />}
              onClick={handlePickFile}
              sx={{ alignSelf: 'flex-start' }}
            >
              Choose another file
            </Button>
          </Stack>
        )}

        {/* Phase 4 — preview */}
        {phase === 'preview' && summary && (
          <Stack spacing={2} sx={{ py: 1 }} data-testid="creator-import-preview-card">
            {/* Replace warning */}
            {isReplace ? (
              <Alert severity="error" data-testid="creator-import-replace-warning">
                <Typography variant="subtitle2" gutterBottom>
                  This will permanently replace this author workspace
                </Typography>
                <Typography variant="body2">
                  «{currentAuthor?.displayName ?? 'Current creator'}» and ALL its data
                  (platforms, posts, expertise pillars, voice, design profiles, templates)
                  will be removed. Cannot be undone.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="info">
                No existing creator in this project — this will be a fresh import.
              </Alert>
            )}

            {/* Summary card */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Importing</Typography>
              <Typography variant="h6">{summary.displayName}</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                <Chip label={`${summary.platformCount} platform${summary.platformCount === 1 ? '' : 's'}`} size="small" />
                <Chip label={`${summary.postCount} post${summary.postCount === 1 ? '' : 's'}`} size="small" />
                <Chip label={`${summary.pillarCount} pillar${summary.pillarCount === 1 ? '' : 's'}`} size="small" />
                <Chip label={`${summary.templatesCount} template${summary.templatesCount === 1 ? '' : 's'}`} size="small" />
                <Chip label={`${summary.examplesCount} example${summary.examplesCount === 1 ? '' : 's'}`} size="small" />
                <Chip label={`${summary.formatsCount} format${summary.formatsCount === 1 ? '' : 's'}`} size="small" />
                <Chip label={`${summary.designProfileSetsCount} design set${summary.designProfileSetsCount === 1 ? '' : 's'}`} size="small" />
                <Chip label={`${summary.designProfilesCount} design profile${summary.designProfilesCount === 1 ? '' : 's'}`} size="small" />
              </Stack>
            </Box>

            <Divider />

            {/* Per-platform breakdown */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Platforms</Typography>
              <List dense disablePadding>
                {summary.platforms.map((p) => (
                  <ListItem key={p.id} disableGutters>
                    <ListItemText
                      primary={p.displayName}
                      secondary={`${p.postCount} post${p.postCount === 1 ? '' : 's'}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            <Divider />

            <Typography variant="caption" color="text.secondary">
              Exported from project <code>{summary.sourceProjectId}</code>{' '}
              on {new Date(summary.exportedAt).toLocaleString()}.
            </Typography>
          </Stack>
        )}

        {/* Phase 5 — importing */}
        {phase === 'importing' && (
          <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">{progressMsg}</Typography>
          </Stack>
        )}

        {/* Phase 6 — success */}
        {phase === 'success' && (
          <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
            <Alert severity="success" sx={{ width: '100%' }}>
              Creator imported successfully.
            </Alert>
          </Stack>
        )}
      </DialogContent>
    </SDialog>
  );
}
